// Imperative inserts into the live script editor, used by the scratchpad's
// "→ Script" promote menu.
//
// These dispatch a ProseMirror transaction on the running editor rather than
// rebuilding the doc — the same approach as per-hunk suggestion accept in
// AdminEditScript. Remounting the editor to add a row would throw away the
// undo stack and the user's cursor mid-session.

import type { Node as PMNode, Schema } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import type { TiptapEditor } from './Editor';

interface InsertOptions {
  /** Ignore the cursor and append at the end of the document (mobile). */
  atEnd?: boolean;
}

/**
 * Where a promoted block should land, as a document position.
 *
 * - Inside a script row → straight after that row, so the new row sits under
 *   the one you were writing in.
 * - Inside any other top-level block (a heading, a loose paragraph) → after
 *   that block.
 * - No usable selection → the end of the document.
 *
 * Pure, so the rule is testable without a browser. `from` is a document
 * position, or null when there is nothing meaningful to insert next to.
 */
export function insertPositionFor(doc: PMNode, from: number | null): number {
  if (from == null) return doc.content.size;
  const clamped = Math.max(0, Math.min(from, doc.content.size));
  const $pos = doc.resolve(clamped);
  for (let depth = $pos.depth; depth > 0; depth--) {
    if ($pos.node(depth).type.name === 'scriptRow') return $pos.after(depth);
  }
  if ($pos.depth >= 1) return $pos.after(1);
  if (clamped === 0 && doc.childCount > 0) return doc.child(0).nodeSize;
  return clamped;
}

/** The cursor position promote should insert next to, or null for "the end". */
function anchorFor(editor: TiptapEditor, atEnd: boolean): number | null {
  if (atEnd) return null;
  const { from } = editor.state.selection;
  // Never clicked into the document: the editor autofocuses at the end on
  // mount, so a doc-start selection with no focus means "nothing chosen".
  if (!editor.isFocused && from <= 1) return null;
  return from;
}

/** Split promoted text into the lines that become paragraphs. */
function textLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function paragraphs(schema: Schema, lines: string[]): PMNode[] {
  return lines.map((line) => schema.nodes.paragraph.create(null, schema.text(line)));
}

/**
 * Focus the editor only when it is actually on screen. On mobile the script
 * column is display:none behind the Script / Scratch switch, and focusing a
 * hidden editor would scroll the page to nowhere.
 */
function focusIfVisible(editor: TiptapEditor) {
  if (editor.view.dom.offsetParent !== null) editor.view.focus();
}

/**
 * Insert the block's text as a new script row: one paragraph per line in the
 * Audio cell tagged Voiceover, with the Visual cell tagged B-roll and left
 * empty, matching a manually added row. The cursor moves into the Audio cell.
 */
export function insertScriptRowAtCursor(
  editor: TiptapEditor,
  audioText: string,
  options: InsertOptions = {},
): boolean {
  const lines = textLines(audioText);
  if (lines.length === 0) return false;

  const schema = editor.schema;
  const rowType = schema.nodes.scriptRow;
  const cellType = schema.nodes.scriptCell;
  if (!rowType || !cellType) return false;

  const body = paragraphs(schema, lines);
  if (body.length === 0) return false;

  const row = rowType.create(null, [
    // Matches insertScriptRowAfter: new rows use Voiceover and B-roll.
    cellType.create({ kind: 'audio', audioType: 'voiceover' }, body),
    cellType.create({ kind: 'visuals', visualType: 'broll' }, [schema.nodes.paragraph.create()]),
  ]);

  let pos = insertPositionFor(editor.state.doc, anchorFor(editor, options.atEnd ?? false));
  let offset = 0;
  for (let index = 0; index < editor.state.doc.childCount; index++) {
    const node = editor.state.doc.child(index);
    const nodeEnd = offset + node.nodeSize;
    if (node.type.name === 'scriptHeaderRow') {
      if (pos <= nodeEnd) pos = nodeEnd;
      break;
    }
    offset = nodeEnd;
  }
  const tr = editor.state.tr.insert(pos, row);
  // pos + 3 lands inside the audio cell's first paragraph: +1 into the row,
  // +1 into the cell, +1 into the paragraph.
  tr.setSelection(TextSelection.near(tr.doc.resolve(pos + 3)));
  tr.scrollIntoView();
  editor.view.dispatch(tr);
  focusIfVisible(editor);
  return true;
}

/**
 * Insert the block's text as plain paragraphs, one per line, at the cursor.
 * The cursor lands at the end of the last inserted paragraph.
 */
export function insertParagraphsAtCursor(
  editor: TiptapEditor,
  text: string,
  options: InsertOptions = {},
): boolean {
  const lines = textLines(text);
  if (lines.length === 0) return false;

  const nodes = paragraphs(editor.schema, lines);
  if (nodes.length === 0) return false;

  const pos = insertPositionFor(editor.state.doc, anchorFor(editor, options.atEnd ?? false));
  const tr = editor.state.tr.insert(pos, nodes);
  const size = nodes.reduce((total, node) => total + node.nodeSize, 0);
  tr.setSelection(TextSelection.near(tr.doc.resolve(pos + size - 1)));
  tr.scrollIntoView();
  editor.view.dispatch(tr);
  focusIfVisible(editor);
  return true;
}
